import React, { useState } from "react";
import { Alert, Badge, Button, FormGroup, Input, Label, Spinner } from "reactstrap";

import { apiFetch, API } from "../../services/api";
import { readError } from "../../helpers/whatsappTemplates";
import { AUTO_PUBLISH, SITE_PREVIEW, defaultPushSend, parisISO, parisParts } from "../../helpers/displaySpaces";

// Contenu d'une réservation : aperçu sur le vrai site, validation / refus (TRC), ou dépôt à la place du partenaire.
// Notification push : TRC corrige titre, texte et lien et choisit l'heure d'envoi, même après validation (tant
// qu'elle n'est pas partie). Sponsoring : fiches choisies parmi celles où le partenaire a une offre active.
const STATUS = { draft: ["Brouillon", "secondary"], submitted: ["À valider", "warning"], approved: ["Validé", "success"], rejected: ["Refusé", "danger"] };
const TEXT_LABELS = { title: "Titre", subtitle: "Sous-titre", text: "Texte", link: "Lien (https)" };
// Fiche de référence pour l'aperçu de la publicité fiche produit.
const PREVIEW_PRODUCT = "nike-pegasus-42-m";

function previewURL(space, token) {
    const es = space.market === "es";
    const host = es ? "https://app.therunningcollective.es" : "https://app.therunningcollective.fr";
    const lang = es ? "es" : "fr";
    const path = space.kind === "listing_card" ? `/${lang}/products/shoes` : space.kind === "product_page_ad" ? `/${lang}/product/${PREVIEW_PRODUCT}` : `/${lang}`;
    return `${host}${path}?displayPreview=${encodeURIComponent(token)}`;
}

function SponsorPicker({ booking, products, setProducts, busy }) {
    const [q, setQ] = useState("");
    const [found, setFound] = useState([]);
    const [error, setError] = useState(null);
    const max = Math.max(1, booking.quantity || 1);
    const search = async () => {
        setError(null);
        const res = await apiFetch(API.back + `/display-sponsor-products?id=${encodeURIComponent(booking.id)}&q=${encodeURIComponent(q)}`, { market: false });
        if (!res.ok) { setError(await readError(res)); return; }
        setFound(await res.json());
    };
    return (
        <div className="mb-3">
            <Label>Fiches sponsorisées ({products.length} / {max})</Label>
            {products.map((slug) => (
                <div key={slug} className="d-flex align-items-center mb-1">
                    <span className="mr-2">{slug}</span>
                    <Button size="sm" outline disabled={busy} onClick={() => setProducts(products.filter((x) => x !== slug))}>Retirer</Button>
                </div>
            ))}
            <div className="d-flex mt-2">
                <Input placeholder="Rechercher une fiche où le partenaire a une offre" value={q} onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") search(); }} />
                <Button className="ml-2" outline disabled={busy || q.trim().length < 2} onClick={search}>Chercher</Button>
            </div>
            {error && <Alert color="danger" className="mt-2 py-2">{error}</Alert>}
            {found.map((p) => (
                <div key={p.slug} className="d-flex align-items-center mt-1">
                    {p.image && <img src={p.image} alt="" width="40" height="40" className="mr-2" style={{ objectFit: "cover" }} />}
                    <span className="mr-2">{p.brand} {p.name} <span className="small text-muted">· {p.colors} coloris</span></span>
                    <Button size="sm" outline color="primary" disabled={busy || products.includes(p.slug) || products.length >= max}
                        onClick={() => setProducts([...products, p.slug])}>{products.includes(p.slug) ? "Choisie" : "Ajouter"}</Button>
                </div>
            ))}
        </div>
    );
}

export function CreativePanel({ booking, space, onChange }) {
    const c = booking.creative || {};
    const isPush = space.kind === "push_notification";
    const isSponsoring = space.kind === "sponsored_product";
    const [form, setForm] = useState({ images: c.images || {}, title: c.title || "", subtitle: c.subtitle || "", text: c.text || "", link: c.link || "", products: c.products || [] });
    const [send, setSend] = useState(c.sendAt ? parisParts(c.sendAt) : defaultPushSend(booking.weeks));
    const [reason, setReason] = useState("");
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState("");

    const call = async (label, path, method, body) => {
        setBusy(label);
        setError(null);
        const res = await apiFetch(API.back + path, { method, market: false, body: JSON.stringify(body) });
        setBusy("");
        if (!res.ok) { setError(await readError(res)); return null; }
        return res.json();
    };

    const upload = async (f, file) => {
        if (!file) return;
        const r = await call(f.key, "/display-creative-upload-url", "POST", { id: booking.id, format: f.key, contentType: file.type, size: file.size });
        if (!r) return;
        const put = await fetch(r.url, { method: "PUT", headers: { "Content-Type": file.type }, body: file });
        if (!put.ok) { setError(`Dépôt S3 refusé (${put.status})`); return; }
        setForm((x) => ({ ...x, images: { ...x.images, [f.key]: r.key } }));
    };

    const save = () => call("save", "/display-creative", "PUT", { id: booking.id, creative: form });

    const preview = async () => {
        if (!(await save())) return;
        const r = await call("preview", "/display-preview-token", "POST", { id: booking.id });
        if (r) window.open(previewURL(space, r.token), "_blank", "noopener");
    };

    const review = async (action) => {
        if (action === "reject" && !reason.trim()) { setError("Motif de refus obligatoire."); return; }
        const body = { id: booking.id, action, reason };
        if (action === "approve") {
            // Corrections TRC appliquées à la validation (recontrôlées par le serveur).
            Object.assign(body, { title: form.title, text: form.text, link: form.link });
            if (isPush) {
                const at = parisISO(send.date, send.time);
                if (!at) { setError("Jour et heure d'envoi obligatoires."); return; }
                body.sendAt = at;
            }
            // Fiches sponsorisées : un nouveau choix doit être enregistré puis redéposé avant validation.
            if (isSponsoring && JSON.stringify(form.products) !== JSON.stringify(c.products || [])) {
                if (!(await save())) return;
                if (!(await call("submit", "/display-creative-submit", "POST", { id: booking.id }))) return;
            }
        }
        if (await call(action, "/display-creative-review", "POST", body)) onChange();
    };

    const depositAndSubmit = async () => {
        if (!(await save())) return;
        if (await call("submit", "/display-creative-submit", "POST", { id: booking.id })) onChange();
    };

    const texts = isSponsoring ? [] : (space.texts || []).map((x) => x.key).filter((k) => TEXT_LABELS[k]);
    const limits = Object.fromEntries((space.texts || []).map((x) => [x.key, x.maxLen]));
    const [label, color] = STATUS[c.status] || ["Aucun contenu", "light"];
    const canReview = c.status === "submitted" || (c.status === "approved" && (isPush || isSponsoring));
    return (
        <div>
            {error && <Alert color="danger">{error}</Alert>}
            <p>Statut : <Badge color={color}>{label}</Badge> {c.submittedBy && <span className="small text-muted">déposé par {c.submittedBy}</span>}
                {c.rejectReason && <span className="small text-danger"> — refusé : {c.rejectReason}</span>}</p>
            {!AUTO_PUBLISH.includes(space.kind) && <Alert color="info" className="py-2">Emplacement diffusé à la main : après validation, le publier dans l'outil concerné.</Alert>}
            {(space.formats || []).map((f) => (
                <FormGroup key={f.key}><Label>{f.label || f.key} — {f.width}×{f.height} {form.images[f.key] && <Badge color="success">déposé</Badge>}</Label>
                    <Input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => upload(f, e.target.files?.[0])} disabled={!!busy} /></FormGroup>
            ))}
            {texts.map((k) => (
                <FormGroup key={k}><Label>{TEXT_LABELS[k]}{limits[k] ? <span className="small text-muted"> ({form[k].length}/{limits[k]})</span> : null}</Label>
                    <Input type={k === "text" && !isPush ? "textarea" : "text"} value={form[k]} maxLength={limits[k] || undefined}
                        onChange={(e) => setForm({ ...form, [k]: e.target.value })} /></FormGroup>
            ))}
            {isSponsoring && <SponsorPicker booking={booking} products={form.products} setProducts={(products) => setForm({ ...form, products })} busy={!!busy} />}
            {isPush && (
                <div className="d-flex flex-wrap mb-2">
                    <FormGroup className="mr-3"><Label>Jour d'envoi</Label><Input type="date" value={send.date} onChange={(e) => setSend({ ...send, date: e.target.value })} /></FormGroup>
                    <FormGroup><Label>Heure (Paris)</Label><Input type="time" value={send.time} onChange={(e) => setSend({ ...send, time: e.target.value })} /></FormGroup>
                    <p className="small text-muted w-100">Dans les semaines réservées, au moins 10 minutes à l'avance. Destinataires : tous les utilisateurs du marché qui reçoivent les notifications.</p>
                </div>
            )}
            <div className="d-flex flex-wrap mb-3">
                {SITE_PREVIEW.includes(space.kind) && <Button outline className="mr-2 mb-2" disabled={!!busy} onClick={preview}>{busy === "preview" ? <Spinner size="sm" /> : "Voir l'aperçu sur le site"}</Button>}
                <Button outline className="mr-2 mb-2" disabled={!!busy} onClick={async () => { if (await save()) onChange(); }}>Enregistrer</Button>
                <Button outline color="primary" className="mb-2" disabled={!!busy} onClick={depositAndSubmit}>Déposer pour validation</Button>
            </div>
            {canReview && (
                <div className="border-top pt-3">
                    {c.status === "approved" && <p className="small text-muted">Déjà validé : tu peux corriger puis revalider{isPush ? " tant que la notification n'est pas partie" : ""}.</p>}
                    {c.status === "submitted" && <FormGroup><Label>Motif (si refus)</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} /></FormGroup>}
                    <Button color="success" className="mr-2" disabled={!!busy} onClick={() => review("approve")}>{busy === "approve" ? <Spinner size="sm" /> : isPush ? "Valider et programmer l'envoi" : "Valider et publier"}</Button>
                    {c.status === "submitted" && <Button color="danger" outline disabled={!!busy} onClick={() => review("reject")}>Refuser</Button>}
                </div>
            )}
        </div>
    );
}
